import { toast } from 'sonner'

/**
 * Generates a stable toast ID from a message string.
 * Same message = same ID = sonner updates existing toast instead of spawning a new one.
 *
 * Uses a simple djb2 hash — fast, dependency-free, deterministic.
 * Not for security — purely for deduplication keying.
 */
export function generateToastId(message: string): string {
  let hash = 5381
  for (let i = 0; i < message.length; i++) {
    hash = (hash * 33) ^ message.charCodeAt(i)
  }
  return `toast-${(hash >>> 0).toString(36)}`
}

const BASE_OPTIONS = {
  duration: 4_000,
  // Sonner updates existing toast when same id is reused — timer resets, no duplicate card.
}

/**
 * Deduplicating toast notification hook.
 *
 * Firing the same message twice does not create two toasts — it updates the
 * existing one (resetting the dismissal timer). This is the correct behavior
 * for repeated status messages, polling results, and form errors.
 *
 * All toasts are dismissible by swipe. Stacking is handled by sonner's Toaster.
 *
 * @example
 * const notify = useNotify()
 * notify.success('Offer claimed!')
 * notify.error('Insufficient balance')
 */
export function useNotify() {
  return {
    success: (message: string) =>
      toast.success(message, { ...BASE_OPTIONS, id: generateToastId(message) }),

    error: (message: string) =>
      toast.error(message, { ...BASE_OPTIONS, id: generateToastId(message), duration: 6_000 }),

    warning: (message: string) =>
      toast.warning(message, { ...BASE_OPTIONS, id: generateToastId(message) }),

    info: (message: string) =>
      toast.info(message, { ...BASE_OPTIONS, id: generateToastId(message) }),

    dismiss: (message: string) =>
      toast.dismiss(generateToastId(message)),
  }
}
